import React from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "../lib/brand";
import { ArrowRight, BookOpen, Users, Sparkles, Heart, Calendar } from "lucide-react";

const PROGRAMS = [
  {
    icon: Users,
    color: "#2196F3",
    title: "Autism Moms Forum",
    description: "A recurring safe-space gathering for moms — featuring guest speakers, resource tables, and the deep relief of being understood.",
  },
  {
    icon: BookOpen,
    color: "#FFB300",
    title: "Caregiver Workshops",
    description: "Free trainings for caregivers, educators, and family members on sensory regulation, advocacy, and the IEP process.",
  },
  {
    icon: Sparkles,
    color: "#E53935",
    title: "Sensory-Friendly Events",
    description: "Picnics, holiday parties, and meet-ups designed with dim lighting, quiet zones, and gentle structure.",
  },
  {
    icon: Heart,
    color: "#4CAF50",
    title: "Family Support Hub",
    description: "Resource referrals, peer mentoring, and one-on-one connection points for families new to the spectrum.",
  },
];

export default function Programs() {
  return (
    <div data-testid="page-programs">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Programs</div>
        <h1 className="mt-4 font-heading font-bold tracking-tighter text-[#1F2937] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
          What we do,<br />
          <span className="brush-underline brush-red">together.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#4B5563] leading-relaxed">
          Every program is shaped by the families we serve. Quietly, joyfully, on purpose.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10">
        <div className="grid md:grid-cols-2 gap-5">
          {PROGRAMS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} data-testid={`program-${p.title.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-3xl bg-white border border-black/5 p-8 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-sm">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${p.color}15`, border: `1px solid ${p.color}33` }}>
                  <Icon className="w-6 h-6" strokeWidth={1.6} style={{ color: p.color }} />
                </div>
                <h3 className="mt-5 font-heading text-2xl font-semibold tracking-tight text-[#1F2937]">{p.title}</h3>
                <p className="mt-3 text-[#4B5563] leading-relaxed">{p.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Event Hall vision */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 rounded-3xl overflow-hidden relative min-h-[400px] border border-black/5">
            <img src={ASSETS.pavilion} alt="Community gathering" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
            <div className="relative z-10 h-full p-10 flex flex-col justify-end text-white">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFB300]">Coming Soon</div>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-semibold tracking-tight">The Joyful Spaces Event Hall</h2>
              <p className="mt-4 max-w-xl text-white/85 leading-relaxed">
                A safe, welcoming, and sensory-friendly venue designed specifically for children with Autism — for birthdays,
                graduations, holidays, and the everyday joys every child deserves.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 rounded-3xl bg-[#F4F1EA] paper-grain p-8 border border-black/5">
            <h3 className="font-heading text-2xl font-semibold tracking-tight text-[#1F2937]">Designed for every sense</h3>
            <ul className="mt-5 space-y-4">
              {[
                "Adjustable lighting and quiet zones",
                "Tactile and visual sensory stations",
                "Trained, neurodiversity-affirming staff",
                "Inclusive activities for every ability",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full mt-2.5 bg-[#2196F3]" />
                  <span className="text-[#1F2937]">{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/donate" data-testid="programs-help-build-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1F2937] hover:bg-black text-white px-6 py-3 text-sm font-semibold">
              Help us build it <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Calendar promo */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24">
        <div className="rounded-3xl bg-white border border-black/5 p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#2196F3]/10 border border-[#2196F3]/30 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#2196F3]" strokeWidth={1.6} />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-semibold tracking-tight text-[#1F2937]">See what's coming up</h3>
              <p className="text-[#6B7280] mt-1">Our latest forums, workshops, and gatherings.</p>
            </div>
          </div>
          <Link to="/events" data-testid="programs-see-events-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#FFB300] hover:bg-[#ffa000] text-[#1F2937] px-6 py-3 text-sm font-semibold">
            View calendar <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
